import React, { useState } from "react";
import {
  Close as CloseIcon,
  Star as StarIcon,
  CreditCard as CreditCardIcon,
  AttachMoney as DollarSignIcon,
  Business as BuildingIcon,
  SwapHoriz as ArrowLeftRightIcon,
  CardGiftcard as GiftIcon,
  Smartphone as SmartphoneIcon,
  ArrowBack as ArrowBackIcon,
  Celebration as CelebrationIcon,
  Event as CalendarIcon,
  Info as InfoIcon
} from "@mui/icons-material";

const DoctorEngagementPaymentModal = ({
  open,
  onClose,
  amount = 8000.0,
  doctorName = "Dr. Wynn",
  email = "abayomimusiliulat@gmail.com"
}) => {
  const [currentStep, setCurrentStep] = useState("payment"); // 'appointment', 'booking', 'payment', or 'rating'
  const [selectedPayment, setSelectedPayment] = useState("");
  const [cardNumber, setCardNumber] = useState("");
  const [expiryDate, setExpiryDate] = useState("");
  const [cvv, setCvv] = useState("");
  const [rememberCard, setRememberCard] = useState(false);

  // Rating states
  const [listeningRating, setListeningRating] = useState(0);
  const [satisfactionRating, setSatisfactionRating] = useState(0);
  const [recommendationRating, setRecommendationRating] = useState(0);
  const [reviewText, setReviewText] = useState("");

  const paymentMethods = [
    {
      id: "card",
      label: "Pay Online (on site)",
      icon: CreditCardIcon,
      color: "orange"
    },
    { id: "ussd", label: "Pay with USSD", icon: SmartphoneIcon },
    { id: "bank", label: "Pay with Bank", icon: BuildingIcon },
    {
      id: "transfer",
      label: "Pay with Bank Transfer",
      icon: ArrowLeftRightIcon
    },
    { id: "barter", label: "Pay with Barter", icon: GiftIcon },
    {
      id: "pockethub",
      label: "Pay with PocketHub",
      icon: DollarSignIcon,
      badge: "Beta"
    }
  ];

  const handlePayment = () => {
    if (selectedPayment) {
      setCurrentStep("booking");
    }
  };

  const handleSubmitReview = () => {
    // Process review submission
    handleClose();
  };

  const handleClose = () => {
    setCurrentStep("appointment");
    setSelectedPayment("");
    setCardNumber("");
    setExpiryDate("");
    setCvv("");
    setRememberCard(false);
    setListeningRating(0);
    setSatisfactionRating(0);
    setRecommendationRating(0);
    setReviewText("");
    onClose();
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  const StarRating = ({ rating, onRatingChange, size = 24 }) => {
    return (
      <div className="star-rating">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            onClick={() => onRatingChange(star)}
            className="star-button"
          >
            <StarIcon
              style={{ fontSize: size }}
              className={star <= rating ? "star-filled" : "star-empty"}
            />
          </button>
        ))}
      </div>
    );
  };

  const NumberRating = ({ rating, onRatingChange }) => {
    return (
      <div className="number-rating">
        {[1, 2, 3, 4, 5].map((num) => (
          <button
            key={num}
            onClick={() => onRatingChange(num)}
            className={`number-button ${
              num <= rating ? "number-selected" : ""
            }`}
          >
            {num}
          </button>
        ))}
      </div>
    );
  };

  if (!open) return null;

  return (
    <div className="pharmacy-modal-overlay" onClick={handleOverlayClick}>
      <div className="pharmacy-modal-container">
        {/* Header - Fixed */}
        <div className="pharmacy-modal-header">
          <div className="pharmacy-header-left">
            {(currentStep === "rating" || currentStep === "payment") && (
              <button
                onClick={() => 
                  currentStep === "rating" 
                    ? setCurrentStep("payment")
                    : setCurrentStep("booking")
                }
                className="pharmacy-back-button"
              >
                <ArrowBackIcon />
              </button>
            )}
            <div
              className={`pharmacy-header-icon ${
                currentStep === "appointment"
                  ? "pharmacy-appointment-icon"
                  : currentStep === "booking"
                  ? "pharmacy-booking-icon"
                  : currentStep === "payment"
                  ? "pharmacy-payment-icon"
                  : "pharmacy-rating-icon"
              }`}
            >
              {currentStep === "appointment" ? (
                <CelebrationIcon className="pharmacy-icon-small" />
              ) : currentStep === "booking" ? (
                <InfoIcon className="pharmacy-icon-small" />
              ) : currentStep === "payment" ? (
                <CreditCardIcon className="pharmacy-icon-small" />
              ) : (
                <div className="pharmacy-rating-icon-container">
                  <StarIcon className="pharmacy-icon-tiny" />
                </div>
              )}
            </div>
          </div>
          <button onClick={handleClose} className="pharmacy-close-button">
            <CloseIcon />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="pharmacy-modal-content">
          {currentStep === "appointment" ? (
            // Appointment Confirmation Step
            <>
              <div className="pharmacy-appointment-content">
                <div className="pharmacy-appointment-icon-container">
                  <CelebrationIcon className="pharmacy-appointment-icon" />
                </div>
                <h2 className="pharmacy-appointment-title">
                  Hello Jessica, good news!
                </h2>
                <p className="pharmacy-appointment-text">
                  Your appointment with Dr. Williams has been confirmed.
                </p>
                
                <button 
                  onClick={() => setCurrentStep("rating")}
                  className="pharmacy-calendar-button"
                >
                  <CalendarIcon className="pharmacy-button-icon" />
                  Add to calendar
                </button>
                
                <button 
                  onClick={() => setCurrentStep("rating")}
                  className="pharmacy-text-button"
                >
                  I will do another time
                </button>
              </div>
            </>
          ) : currentStep === "booking" ? (
            // Booking Step
            <>
              <div className="pharmacy-booking-content">
                <div className="pharmacy-booking-icon-container">
                  <InfoIcon className="pharmacy-booking-icon" />
                </div>
                <h2 className="pharmacy-booking-title">
                  Hi, you'll be able to rate Dr. Williams today only after you have had an engagement with him.
                </h2>
                <p className="pharmacy-booking-text">
                  Would you like to book him?
                </p>
                
                <button 
                  onClick={() => setCurrentStep("appointment")}
                  className="pharmacy-yes-button"
                >
                  Yes
                </button>
                
                <button 
                  onClick={() => setCurrentStep("appointment")}
                  className="pharmacy-text-button"
                >
                  I will do it another time
                </button>
              </div>
            </>
          ) : currentStep === "payment" ? (
            // Payment Step
            <>
              <div className="pharmacy-payment-title">
                <h2 className="pharmacy-title">My Payment Title</h2>
                <div className="pharmacy-email-container">
                  <div className="pharmacy-email-indicator"></div>
                  <span className="pharmacy-email-text">{email}</span>
                </div>
              </div>

              {/* Amount Display */}
              <div className="pharmacy-amount-display">
                <div className="pharmacy-amount-text">
                  ₦ {amount.toLocaleString()}.00
                </div>
              </div>

              {/* Card Details */}
              <div className="pharmacy-card-details">
                <div className="pharmacy-input-group">
                  <label className="pharmacy-input-label">CARD NUMBER</label>
                  <input
                    type="text"
                    placeholder="0000 0000 0000 0000"
                    value={cardNumber}
                    onChange={(e) => setCardNumber(e.target.value)}
                    className="pharmacy-input-field"
                  />
                </div>

                <div className="pharmacy-input-row">
                  <div className="pharmacy-input-group">
                    <label className="pharmacy-input-label">VALID TILL</label>
                    <input
                      type="text"
                      placeholder="MM / YY"
                      value={expiryDate}
                      onChange={(e) => setExpiryDate(e.target.value)}
                      className="pharmacy-input-field"
                    />
                  </div>
                  <div className="pharmacy-input-group">
                    <label className="pharmacy-input-label">CVV</label>
                    <input
                      type="text"
                      placeholder="000"
                      value={cvv}
                      onChange={(e) => setCvv(e.target.value)}
                      className="pharmacy-input-field"
                    />
                  </div>
                </div>

                <div className="pharmacy-checkbox-container">
                  <input
                    type="checkbox"
                    id="remember"
                    checked={rememberCard}
                    onChange={(e) => setRememberCard(e.target.checked)}
                    className="pharmacy-checkbox"
                  />
                  <label htmlFor="remember" className="pharmacy-checkbox-label">
                    Remember this card next time
                  </label>
                </div>
              </div>

              {/* Payment Methods */}
              <div className="pharmacy-payment-methods">
                {paymentMethods.map((method) => {
                  const IconComponent = method.icon;
                  return (
                    <button
                      key={method.id}
                      onClick={() => setSelectedPayment(method.id)}
                      className={`pharmacy-payment-method ${
                        selectedPayment === method.id ? "selected" : ""
                      }`}
                    >
                      <div className="pharmacy-method-left">
                        <div
                          className={`pharmacy-method-icon ${
                            method.color === "orange"
                              ? "pharmacy-orange-bg"
                              : "pharmacy-gray-bg"
                          }`}
                        >
                          <IconComponent
                            className={
                              method.color === "orange"
                                ? "pharmacy-white-icon"
                                : "pharmacy-gray-icon"
                            }
                          />
                        </div>
                        <span className="pharmacy-method-label">
                          {method.label}
                        </span>
                        {method.badge && (
                          <span className="pharmacy-method-badge">
                            {method.badge}
                          </span>
                        )}
                      </div>
                      <div
                        className={`pharmacy-radio-button ${
                          selectedPayment === method.id
                            ? "pharmacy-radio-selected"
                            : ""
                        }`}
                      >
                        {selectedPayment === method.id && (
                          <div className="pharmacy-radio-inner"></div>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Action Buttons */}
              <div className="pharmacy-action-buttons">
                <button
                  onClick={handlePayment}
                  disabled={!selectedPayment}
                  className={`pharmacy-pay-button ${
                    selectedPayment ? "enabled" : "disabled"
                  }`}
                >
                  Pay Now
                </button>
                <button
                  onClick={handleClose}
                  className="pharmacy-cancel-button"
                >
                  Cancel Order
                </button>
              </div>
            </>
          ) : (
            // Rating Step
            <>
              <div className="pharmacy-rating-header">
                <div className="pharmacy-rating-avatar">
                  <div className="pharmacy-rating-avatar-inner">
                    <StarIcon className="pharmacy-rating-star" />
                  </div>
                </div>
                <h2 className="pharmacy-rating-title">
                  Rate your experience with {doctorName}
                </h2>
              </div>

              <div className="pharmacy-rating-content">
                {/* Listening Rating */}
                <div className="pharmacy-rating-section">
                  <p className="pharmacy-rating-question">
                    How well did the doctor listen to your concerns and explain
                    things in a clear, understandable way?
                  </p>
                  <StarRating
                    rating={listeningRating}
                    onRatingChange={setListeningRating}
                  />
                </div>

                {/* Satisfaction Rating */}
                <div className="pharmacy-rating-section">
                  <p className="pharmacy-rating-question">
                    How satisfied are you with the care and treatment provided
                    during your visit?
                  </p>
                  <StarRating
                    rating={satisfactionRating}
                    onRatingChange={setSatisfactionRating}
                  />
                </div>

                {/* Recommendation Rating */}
                <div className="pharmacy-rating-section">
                  <p className="pharmacy-rating-question">
                    How likely are you to recommend this doctor to family or
                    friends?
                  </p>
                  <NumberRating
                    rating={recommendationRating}
                    onRatingChange={setRecommendationRating}
                  />
                  <p className="pharmacy-rating-note">
                    (Rating: 1 - Not likely at all, 5 - Extremely likely)
                  </p>
                </div>

                {/* Review Text */}
                <div className="pharmacy-rating-section">
                  <p className="pharmacy-rating-question">
                    Tell us more about his service
                  </p>
                  <textarea
                    value={reviewText}
                    onChange={(e) => setReviewText(e.target.value)}
                    placeholder="Write your review here..."
                    rows={4}
                    className="pharmacy-review-textarea"
                  />
                </div>

                {/* Submit Button */}
                <button
                  onClick={handleSubmitReview}
                  className="pharmacy-submit-button"
                >
                  Submit Review
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default DoctorEngagementPaymentModal;